import {
  MaintenancePlanBulkImportField,
  MaintenancePlanBulkImportRow,
} from "@boark/shared/types/maintenance-plan-bulk-import";
import { useQueryClient } from "@tanstack/react-query";
import { useMemo, useState } from "react";

import { bulkImportMaintenancePlans } from "../services/maintenance-plan";
import { parseDelimitedText } from "../utils/parse-delimited-text";
import { parseMaintenancePlanActionDate } from "../utils/parse-maintenance-plan-action-date";
import { useLocale } from "../stores/locale";
import { Button } from "./button";
import { Modal } from "./modal";
import { Select } from "./select";

const COLUMN_IGNORE = "ignore";

type ColumnMapping = MaintenancePlanBulkImportField | typeof COLUMN_IGNORE;

type ParsedTable = {
  rows: string[][];
  columnCount: number;
};

function parseNumber(value: string): number | null {
  const normalized = value.trim().replace(/\s/g, "").replace(",", ".");
  if (normalized === "") {
    return null;
  }
  const parsed = Number(normalized);
  return Number.isFinite(parsed) ? parsed : null;
}

type BuildImportRowsResult =
  | { rows: MaintenancePlanBulkImportRow[]; dateParseError: false }
  | { rows: null; dateParseError: boolean };

function buildImportRows({
  table,
  columnMappings,
}: {
  table: ParsedTable;
  columnMappings: ColumnMapping[];
}): BuildImportRowsResult {
  const fieldIndexes = new Map<MaintenancePlanBulkImportField, number>();

  columnMappings.forEach((mapping, index) => {
    if (mapping !== COLUMN_IGNORE) {
      fieldIndexes.set(mapping, index);
    }
  });

  const requiredFields: MaintenancePlanBulkImportField[] = [
    "groupCode",
    "groupName",
    "propertyName",
    "propertyPartName",
    "propertyPartCode",
    "description",
  ];

  if (!requiredFields.every((field) => fieldIndexes.has(field))) {
    return { rows: null, dateParseError: false };
  }

  const rows: MaintenancePlanBulkImportRow[] = [];
  let dateParseError = false;

  for (const cells of table.rows) {
    const getValue = (field: MaintenancePlanBulkImportField) => {
      const index = fieldIndexes.get(field);
      return index === undefined ? "" : (cells[index] ?? "").trim();
    };

    const costValue = getValue("cost");
    const cost =
      costValue === "" ? undefined : parseNumber(costValue);
    const yearlyFrequencyValue = getValue("yearlyFrequency");
    const yearlyFrequency =
      yearlyFrequencyValue === ""
        ? undefined
        : parseNumber(yearlyFrequencyValue);
    const lastActionValue = getValue("lastAction");
    const lastAction =
      lastActionValue === ""
        ? undefined
        : parseMaintenancePlanActionDate(lastActionValue);

    if (
      (costValue !== "" && cost === null) ||
      (yearlyFrequencyValue !== "" && yearlyFrequency === null)
    ) {
      continue;
    }

    if (lastActionValue !== "" && lastAction === null) {
      dateParseError = true;
      continue;
    }

    const groupCode = getValue("groupCode");
    const groupName = getValue("groupName");
    const propertyName = getValue("propertyName");
    const propertyPartName = getValue("propertyPartName");
    const propertyPartCode = getValue("propertyPartCode");

    if (
      groupCode === "" ||
      groupName === "" ||
      propertyName === "" ||
      propertyPartName === "" ||
      propertyPartCode === ""
    ) {
      continue;
    }

    rows.push({
      groupCode,
      groupName,
      propertyName,
      propertyPartName,
      propertyPartCode,
      description: getValue("description"),
      ...(cost != null && { cost }),
      ...(yearlyFrequency != null && { yearlyFrequency }),
      ...(lastAction != null && { lastAction }),
    });
  }

  if (dateParseError) {
    return { rows: null, dateParseError: true };
  }

  return rows.length > 0
    ? { rows, dateParseError: false }
    : { rows: null, dateParseError: false };
}

export function MaintenancePlanImportModal({
  onClose,
}: {
  onClose: () => void;
}) {
  const { locale: t } = useLocale();
  const queryClient = useQueryClient();
  const [pasteText, setPasteText] = useState("");
  const [parsedTable, setParsedTable] = useState<ParsedTable | null>(null);
  const [columnMappings, setColumnMappings] = useState<ColumnMapping[]>([]);
  const [loading, setLoading] = useState(false);
  const [hasError, setHasError] = useState(false);
  const [resultMessage, setResultMessage] = useState<string | null>(null);

  const columnOptions = useMemo(
    () => [
      { value: COLUMN_IGNORE, label: t.maintenance.importIgnoreColumn },
      { value: "groupCode", label: t.maintenance.importGroupCode },
      { value: "groupName", label: t.maintenance.importGroupName },
      { value: "propertyName", label: t.maintenance.importPropertyName },
      {
        value: "propertyPartName",
        label: t.maintenance.importPropertyPartName,
      },
      {
        value: "propertyPartCode",
        label: t.maintenance.importPropertyPartCode,
      },
      { value: "description", label: t.maintenance.description },
      { value: "cost", label: t.maintenance.cost },
      { value: "yearlyFrequency", label: t.maintenance.yearlyFrequency },
      { value: "lastAction", label: t.maintenance.importLastAction },
    ],
    [t],
  );

  const previewRows = parsedTable?.rows.slice(0, 10) ?? [];
  const importResult = useMemo(() => {
    if (!parsedTable) {
      return null;
    }
    return buildImportRows({ table: parsedTable, columnMappings });
  }, [parsedTable, columnMappings]);
  const importRows = importResult?.rows ?? null;
  const dateParseError = importResult?.dateParseError ?? false;

  const mappedFieldCount = columnMappings.filter(
    (mapping) => mapping !== COLUMN_IGNORE,
  ).length;

  return (
    <Modal open size="lg" onClose={onClose}>
      <div className="flex flex-col gap-4 min-h-0 flex-1">
        <h2 className="text-xl font-semibold pr-8">{t.maintenance.importTitle}</h2>

        {!parsedTable && (
          <>
            <p className="text-sm text-gray-600">{t.maintenance.importPasteHelp}</p>
            <textarea
              className="w-full min-h-48 border border-gray-300 rounded-xs p-2 font-mono text-sm"
              placeholder={t.maintenance.importPastePlaceholder}
              value={pasteText}
              onChange={(event) => setPasteText(event.target.value)}
            />
            <div className="flex justify-end gap-2">
              <Button type="button" onClick={onClose}>
                {t.cancel}
              </Button>
              <Button
                disabled={pasteText.trim() === ""}
                type="button"
                onClick={() => {
                  const rows = parseDelimitedText(pasteText);
                  if (rows.length === 0) {
                    return;
                  }
                  const columnCount = Math.max(...rows.map((row) => row.length));
                  setParsedTable({ rows, columnCount });
                  setColumnMappings(
                    Array.from({ length: columnCount }, () => COLUMN_IGNORE),
                  );
                  setResultMessage(null);
                  setHasError(false);
                }}
              >
                {t.maintenance.importParse}
              </Button>
            </div>
          </>
        )}

        {parsedTable && (
          <>
            <p className="text-sm text-gray-600">
              {t.maintenance.importMapHelp}
            </p>
            <div className="overflow-x-auto border border-gray-300 rounded-xs">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-gray-50 border-b border-gray-300">
                    {Array.from({ length: parsedTable.columnCount }).map(
                      (_, columnIndex) => (
                        <th
                          key={columnIndex}
                          className="p-2 text-left font-medium min-w-40"
                        >
                          <Select
                            allowEmpty
                            options={columnOptions}
                            value={columnMappings[columnIndex] ?? COLUMN_IGNORE}
                            onChange={(value) => {
                              setColumnMappings((current) => {
                                const next = [...current];
                                next[columnIndex] = value as ColumnMapping;
                                return next;
                              });
                            }}
                          />
                        </th>
                      ),
                    )}
                  </tr>
                </thead>
                <tbody>
                  {previewRows.map((row, rowIndex) => (
                    <tr
                      key={rowIndex}
                      className="border-b border-gray-200 last:border-b-0"
                    >
                      {Array.from({ length: parsedTable.columnCount }).map(
                        (_, columnIndex) => (
                          <td key={columnIndex} className="p-2 align-top">
                            {row[columnIndex] ?? ""}
                          </td>
                        ),
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {parsedTable.rows.length > previewRows.length && (
              <p className="text-sm text-gray-500">
                {t.maintenance.importPreviewRows
                  .replace("{shown}", String(previewRows.length))
                  .replace("{total}", String(parsedTable.rows.length))}
              </p>
            )}
            {importRows && (
              <p className="text-sm text-gray-600">
                {t.maintenance.importReadyRows.replace(
                  "{count}",
                  String(importRows.length),
                )}
              </p>
            )}
            {!importRows && !dateParseError && (
              <p className="text-sm text-amber-700">
                {t.maintenance.importMappingIncomplete}
              </p>
            )}
            {dateParseError && (
              <p className="text-sm text-amber-700">
                {t.maintenance.importDateParseError}
              </p>
            )}
            {hasError && (
              <p className="text-sm text-red-600">{t.maintenance.importError}</p>
            )}
            {resultMessage && (
              <p className="text-sm text-green-700">{resultMessage}</p>
            )}
            <div className="flex justify-between gap-2">
              <Button
                type="button"
                onClick={() => {
                  setParsedTable(null);
                  setColumnMappings([]);
                  setHasError(false);
                  setResultMessage(null);
                }}
              >
                {t.maintenance.importBack}
              </Button>
              <div className="flex gap-2">
                <Button type="button" onClick={onClose}>
                  {t.cancel}
                </Button>
                <Button
                  disabled={
                    loading ||
                    !importRows ||
                    dateParseError ||
                    mappedFieldCount === 0
                  }
                  loading={loading}
                  type="button"
                  onClick={() => {
                    if (!importRows) {
                      setHasError(true);
                      return;
                    }

                    void (async () => {
                      setLoading(true);
                      setHasError(false);
                      setResultMessage(null);
                      try {
                        const result = await bulkImportMaintenancePlans({
                          rows: importRows,
                        });
                        await queryClient.invalidateQueries({
                          queryKey: ["maintenancePlans"],
                        });
                        await queryClient.invalidateQueries({
                          queryKey: ["maintenancePlanGroups"],
                        });
                        await queryClient.invalidateQueries({
                          queryKey: ["propertyParts"],
                        });
                        await queryClient.invalidateQueries({
                          queryKey: ["properties"],
                        });
                        setResultMessage(
                          t.maintenance.importSuccess
                            .replace("{created}", String(result.created))
                            .replace("{skipped}", String(result.skipped)),
                        );
                      } catch {
                        setHasError(true);
                      } finally {
                        setLoading(false);
                      }
                    })();
                  }}
                >
                  {t.maintenance.importSubmit}
                </Button>
              </div>
            </div>
          </>
        )}
      </div>
    </Modal>
  );
}
